/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useMemo, useState } from 'react';
import { Card, Badge, Modal, ModalHeader, ModalBody } from 'reactstrap';
import type { InsightSynthesis } from '@g17eco/types/insights-synthesis';
import { Chart } from 'react-google-charts';
import { getPriorityColor } from './utils/helpers';
import { InsightCard } from './partials/InsightCard';
import { InsightViewModal } from './partials/InsightViewModal';

interface Props {
  insights: InsightSynthesis[];
}

const CHART_VERSION = '51' as 'current';

// Helper function to resolve CSS custom properties to actual color values
const resolveColor = (cssVar: string): string => {
  if (typeof window === 'undefined') return cssVar;
  const match = cssVar.match(/var\((--theme-[^)]+)\)/);
  if (!match) return cssVar;
  return getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() || cssVar;
};

const CHART_COLORS = {
  // Priority/Impact levels
  critical: 'var(--theme-DangerMedium)',
  high: 'var(--theme-WarningMedium)',
  medium: 'var(--theme-WarningLight)',
  low: 'var(--theme-InfoLight)',
  lowSuccess: 'var(--theme-SuccessMedium)', // low priority / success state
  // SWOT categories
  strength: 'var(--theme-SuccessMedium)',
  weakness: 'var(--theme-DangerMedium)',
  opportunity: 'var(--theme-InfoLight)',
  threat: 'var(--theme-WarningMedium)',
  // Time horizon
  immediate: 'var(--theme-DangerMedium)',
  shortTerm: 'var(--theme-WarningMedium)',
  mediumTerm: 'var(--theme-WarningLight)',
  longTerm: 'var(--theme-InfoLight)',
  // EESG categories
  environment: 'var(--theme-SuccessMedium)',
  economic: 'var(--theme-AccentMedium)',
  social: 'var(--theme-InfoLight)',
  governance: 'var(--theme-WarningMedium)',
  // Treemap/heatmap scale
  treemapMin: 'var(--theme-SuccessMedium)',
  treemapMid: 'var(--theme-WarningMedium)',
  treemapMax: 'var(--theme-DangerMedium)',
  // Impact Assessment
  financial: 'var(--theme-SuccessMedium)',
  regulatory: 'var(--theme-WarningMedium)',
  reputational: 'var(--theme-DangerMedium)',
  // UI
  white: 'var(--theme-BgExtralight)',
  black: 'var(--theme-TextDark)',
  tooltipOverlay: 'rgba(0, 0, 0, 0.9)',
  lightGray: 'var(--theme-BgLight)',
  borderGray: 'var(--theme-BorderDefault)',
  gridGray: 'var(--theme-NeutralsLight)',
  textGray: 'var(--theme-TextMedium)',
  darkGray: 'var(--theme-TextDark)',
  // Bootstrap semantic (for getPriorityColor mapping in scatter)
  danger: 'var(--theme-DangerMedium)',
  warning: 'var(--theme-WarningMedium)',
  info: 'var(--theme-WarningLight)',
  success: 'var(--theme-SuccessMedium)',
  secondary: 'var(--theme-NeutralsMedium)',
  // RGB for heatmap interpolation (green → yellow → red)
  rgbGreen: { r: 14, g: 147, b: 129 }, // theme-SuccessMedium
  rgbYellow: { r: 241, g: 136, b: 60 }, // theme-WarningMedium
  rgbRed: { r: 220, g: 53, b: 69 }, // theme-DangerMedium
} as const;


export const PrioritiesView: React.FC<Props> = ({ insights }) => {
  const [selectedInsight, setSelectedInsight] = useState<InsightSynthesis | null>(null);
  const [selectedInsightsList, setSelectedInsightsList] = useState<InsightSynthesis[] | null>(null);
  const [matrixModalTitle, setMatrixModalTitle] = useState<string>('');
  // Priority Distribution Data
  const priorityDistribution = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    insights.forEach((insight) => {
      counts[insight.content.impact]++;
    });
    return [
      ['Priority', 'Count'],
      ['Critical', counts.critical],
      ['High', counts.high],
      ['Medium', counts.medium],
      ['Low', counts.low],
    ];
  }, [insights]);

  // SWOT Category Distribution
  const swotDistribution = useMemo(() => {
    const counts = { strength: 0, weakness: 0, opportunity: 0, threat: 0 };
    insights.forEach((insight) => {
      counts[insight.content.swotCategory]++;
    });
    return [
      ['SWOT Category', 'Count', { role: 'style' }],
      ['Strengths', counts.strength, resolveColor(CHART_COLORS.strength)],
      ['Weaknesses', counts.weakness, resolveColor(CHART_COLORS.weakness)],
      ['Opportunities', counts.opportunity, resolveColor(CHART_COLORS.opportunity)],
      ['Threats', counts.threat, resolveColor(CHART_COLORS.threat)],
    ];
  }, [insights]);

  // Time Horizon Distribution
  const timeHorizonDistribution = useMemo(() => {
    const counts = { immediate: 0, short_term: 0, medium_term: 0, long_term: 0 };
    insights.forEach((insight) => {
      counts[insight.content.timeHorizon]++;
    });
    return [
      ['Time Horizon', 'Count', { role: 'style' }],
      ['Immediate', counts.immediate, resolveColor(CHART_COLORS.immediate)],
      ['Short Term', counts.short_term, resolveColor(CHART_COLORS.shortTerm)],
      ['Medium Term', counts.medium_term, resolveColor(CHART_COLORS.mediumTerm)],
      ['Long Term', counts.long_term, resolveColor(CHART_COLORS.longTerm)],
    ];
  }, [insights]);

  // Impact Assessment Averages
  const impactAssessment = useMemo(() => {
    if (insights.length === 0) return [['Category', 'Score'], ['Financial', 0], ['Regulatory', 0], ['Reputational', 0]];

    const totals = { financial: 0, regulatory: 0, reputational: 0 };
    insights.forEach((insight) => {
      totals.financial += insight.content.impactAssessment.financial;
      totals.regulatory += insight.content.impactAssessment.regulatory;
      totals.reputational += insight.content.impactAssessment.reputational;
    });

    return [
      ['Category', 'Score', { role: 'style' }],
      ['Financial', totals.financial / insights.length, resolveColor(CHART_COLORS.financial)],
      ['Regulatory', totals.regulatory / insights.length, resolveColor(CHART_COLORS.regulatory)],
      ['Reputational', totals.reputational / insights.length, resolveColor(CHART_COLORS.reputational)],
    ];
  }, [insights]);

  // Top Priority Insights
  const topInsights = useMemo(() => {
    return [...insights]
      .sort((a, b) => b.content.priorityScore - a.content.priorityScore)
      .slice(0, 10);
  }, [insights]);

  // Actions Scatter Data (Effort vs Impact)
  const actionsScatterData = useMemo(() => {
    const data: any[] = [['Effort', 'Impact', { role: 'style' }, { role: 'tooltip' }]];
    const insightMap: InsightSynthesis[] = [];

    // Group by priority to get consistent colors
    const priorityGroups: Record<string, Array<{ effort: number; impact: number; insight: InsightSynthesis; action: any }>> = {
      critical: [],
      high: [],
      medium: [],
      low: [],
    };

    insights.forEach((insight) => {
      insight.content.recommendations.forEach((action) => {
        const priority = insight.content.impact;
        priorityGroups[priority].push({
          effort: action.effortRequired || 5,
          impact: action.impactLevel || 5,
          insight,
          action,
        });
      });
    });

    // Add data in priority order so colors match legend
    const colors = { critical: resolveColor(CHART_COLORS.critical), high: resolveColor(CHART_COLORS.high), medium: resolveColor(CHART_COLORS.medium), low: resolveColor(CHART_COLORS.lowSuccess) };
    ['critical', 'high', 'medium', 'low'].forEach((priority) => {
      priorityGroups[priority].forEach((item) => {
        insightMap.push(item.insight);
        data.push([
          item.effort,
          item.impact,
          `point { fill-color: ${colors[priority as keyof typeof colors]}; }`,
          `${item.action.title}\nEffort: ${item.effort.toFixed(1)} | Impact: ${item.impact.toFixed(1)}\nFrom: ${item.insight.content.title}`,
        ]);
      });
    });

    return { data, insights: insightMap };
  }, [insights]);

  // EESG (Section Type) Distribution
  const eesgDistribution = useMemo(() => {
    const counts = { environment: 0, economic: 0, social: 0, governance: 0 };
    insights.forEach((insight) => {
      const sectionType = insight.source.metadata.sectionType;
      if (sectionType in counts) {
        counts[sectionType]++;
      }
    });
    return [
      ['EESG Category', 'Count', { role: 'style' }],
      ['Environment', counts.environment, resolveColor(CHART_COLORS.environment)],
      ['Economic', counts.economic, resolveColor(CHART_COLORS.economic)],
      ['Social', counts.social, resolveColor(CHART_COLORS.social)],
      ['Governance', counts.governance, resolveColor(CHART_COLORS.governance)],
    ];
  }, [insights]);

  // Actions by Impact, Effort, Control
  const actionsMetrics = useMemo(() => {
    const allActions = insights.flatMap((insight) => insight.content.recommendations);

    const impactCounts = { low: 0, medium: 0, high: 0, critical: 0 };
    const effortCounts = { low: 0, medium: 0, high: 0 };
    const controlCounts = { low: 0, medium: 0, high: 0 };

    allActions.forEach((action) => {
      // Impact levels (0-10 scale)
      const impact = action.impactLevel || 0;
      if (impact < 3) impactCounts.low++;
      else if (impact < 5) impactCounts.medium++;
      else if (impact < 8) impactCounts.high++;
      else impactCounts.critical++;

      // Effort levels
      const effort = action.effortRequired || 0;
      if (effort < 4) effortCounts.low++;
      else if (effort < 7) effortCounts.medium++;
      else effortCounts.high++;

      // Control levels
      const control = action.controlLevel || 0;
      if (control < 4) controlCounts.low++;
      else if (control < 7) controlCounts.medium++;
      else controlCounts.high++;
    });

    return {
      impact: [
        ['Impact Level', 'Count', { role: 'style' }],
        ['Low', impactCounts.low, resolveColor(CHART_COLORS.lowSuccess)],
        ['Medium', impactCounts.medium, resolveColor(CHART_COLORS.medium)],
        ['High', impactCounts.high, resolveColor(CHART_COLORS.high)],
        ['Critical', impactCounts.critical, resolveColor(CHART_COLORS.critical)],
      ],
      effort: [
        ['Effort Level', 'Count', { role: 'style' }],
        ['Low', effortCounts.low, resolveColor(CHART_COLORS.lowSuccess)],
        ['Medium', effortCounts.medium, resolveColor(CHART_COLORS.medium)],
        ['High', effortCounts.high, resolveColor(CHART_COLORS.critical)],
      ],
      control: [
        ['Control Level', 'Count', { role: 'style' }],
        ['Low', controlCounts.low, resolveColor(CHART_COLORS.critical)],
        ['Medium', controlCounts.medium, resolveColor(CHART_COLORS.medium)],
        ['High', controlCounts.high, resolveColor(CHART_COLORS.lowSuccess)],
      ],
    };
  }, [insights]);

  // EESG Treemap Data - Multi-dimensional visualization
  // Hierarchy: EESG → categoryName
  const eesgTreemapData = useMemo(() => {
    // Structure: [ID, Parent, Size (count), Color Value (SWOT numeric)]
    const data: any[] = [
      ['ID', 'Parent', 'Count', 'SWOT Value'],
      ['EESG', null, 0, 0],
    ];

    // Map to store insights for each node
    type NodeData = {
      count: number;
      swotValue: number;
      insights: InsightSynthesis[];
    };

    const nodeMap: Record<string, NodeData> = {};

    // Map to store SWOT breakdown for each display label (for tooltip)
    const swotBreakdownMap: Record<string, { strength: number; opportunity: number; weakness: number; threat: number }> = {};

    // EESG labels
    const eesgLabels = {
      environment: 'Environment',
      economic: 'Economic',
      social: 'Social',
      governance: 'Governance',
    };

    // SWOT numeric values for coloring (positive to negative scale)
    const swotValues = {
      strength: 2,      // Most positive (greenest)
      opportunity: 1,   // Positive (light green)
      weakness: -1,     // Negative (light red/orange)
      threat: -2,       // Most negative (reddest)
    };

    // Build the hierarchy: EESG → CategoryName
    insights.forEach((insight) => {
      const eesgKey = insight.source.metadata.sectionType;
      const categoryName = insight.source.categoryName || 'Uncategorized';
      const swotValue = swotValues[insight.content.swotCategory];

      if (!(eesgKey in eesgLabels)) return;

      // EESG level (top level)
      if (!nodeMap[eesgKey]) {
        nodeMap[eesgKey] = { count: 0, swotValue: 0, insights: [] };
      }
      nodeMap[eesgKey].count++;
      nodeMap[eesgKey].swotValue += swotValue;
      nodeMap[eesgKey].insights.push(insight);

      // EESG → CategoryName level (leaf nodes)
      const fullId = `${eesgKey}-${categoryName}`;
      if (!nodeMap[fullId]) {
        nodeMap[fullId] = { count: 0, swotValue: 0, insights: [] };
      }
      nodeMap[fullId].count++;
      nodeMap[fullId].swotValue += swotValue;
      nodeMap[fullId].insights.push(insight);
    });

    // Add EESG nodes (top level)
    Object.entries(eesgLabels).forEach(([key, label]) => {
      if (nodeMap[key] && nodeMap[key].count > 0) {
        // Calculate SWOT breakdown for this EESG category
        const breakdown = { strength: 0, opportunity: 0, weakness: 0, threat: 0 };
        nodeMap[key].insights.forEach((insight) => {
          breakdown[insight.content.swotCategory]++;
        });
        swotBreakdownMap[label] = breakdown;

        data.push([
          label, // Use readable label
          'EESG',
          0, // Parent nodes have 0 size
          nodeMap[key].swotValue / nodeMap[key].count, // Average SWOT value
        ]);
      }
    });

    // Add EESG → CategoryName leaf nodes
    Object.keys(eesgLabels).forEach((eesgKey) => {
      if (nodeMap[eesgKey]) {
        const categories = new Set<string>();
        nodeMap[eesgKey].insights.forEach((insight) => {
          categories.add(insight.source.categoryName || 'Uncategorized');
        });

        categories.forEach((categoryName) => {
          const fullId = `${eesgKey}-${categoryName}`;
          if (nodeMap[fullId] && nodeMap[fullId].count > 0) {
            const displayLabel = `${categoryName} (${nodeMap[fullId].count})`;

            // Calculate SWOT breakdown for this leaf node
            const breakdown = { strength: 0, opportunity: 0, weakness: 0, threat: 0 };
            nodeMap[fullId].insights.forEach((insight) => {
              breakdown[insight.content.swotCategory]++;
            });
            swotBreakdownMap[displayLabel] = breakdown;

            data.push([
              displayLabel, // Leaf label with count
              eesgLabels[eesgKey as keyof typeof eesgLabels], // Parent is EESG label
              nodeMap[fullId].count,
              nodeMap[fullId].swotValue / nodeMap[fullId].count, // Average SWOT value
            ]);
          }
        });
      }
    });

    return { data, nodeMap, swotBreakdownMap };
  }, [insights]);

  if (insights.length === 0) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-chart-bar fa-3x text-ThemeTextLight mb-3' />
        <h4 className='text-ThemeTextDark mb-2'>No Priority Data Available</h4>
        <p className='text-ThemeTextMedium'>
          Priority visualizations will appear here once insights are generated.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}

      {/* EESG Treemap Section */}
      <div className='py-4 border-top'>
        <div className='d-flex gap-2'>
          <div>
            <Badge className='background-ThemeSuccessExtralight p-2'>
              <i className='text-ThemeSuccessDark fal fa-2x fa-sitemap' />
            </Badge>
          </div>
          <div className='d-flex flex-column ms-2'>
            <div className='text-xl strong'>EESG Hierarchical Analysis</div>
            <div className='text-ThemeTextMedium text-md'>
              Hierarchical view showing EESG categories → Specific topics. Box size represents insight count, color shows SWOT distribution (green=strength, red=weakness).
            </div>
          </div>
        </div>
      </div>
      <div className='row g-4 mb-4'>
        {/* EESG Treemap */}
        <div className='col-12'>
          <Card>
            <div className='p-3'>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='TreeMap'
                data={eesgTreemapData.data}
                options={{
                  minColor: resolveColor(CHART_COLORS.treemapMin),
                  midColor: resolveColor(CHART_COLORS.treemapMid),
                  maxColor: resolveColor(CHART_COLORS.treemapMax),
                  headerHeight: 0, // Taller headers for better hierarchy
                  maxDepth: 2, // Show both EESG and Category levels
                  maxPostDepth: 0, // Don't show additional levels beyond maxDepth
                  hintOpacity: 0, // Slightly transparent borders
                  highlightOnMouseOver: true,
                  generateTooltip: (row: number, size: number, value: number) => {
                    const rowData = eesgTreemapData.data[row + 1];
                    if (!rowData) return '';
                    const displayLabel = rowData[0];
                    const breakdown = eesgTreemapData.swotBreakdownMap[displayLabel];

                    let swotBreakdown = '';
                    if (breakdown) {
                      swotBreakdown = `
                        <div style="margin-top:8px; padding-top:8px; border-top:1px solid ${CHART_COLORS.borderGray};">
                          <div style="font-size:11px; margin-bottom:4px; opacity:0.8;">SWOT Breakdown:</div>
                          <div style="display:grid; grid-template-columns: 1fr 1fr; gap:4px; font-size:11px;">
                            <div><span style="color:${resolveColor(CHART_COLORS.strength)};">●</span> Strengths: ${breakdown.strength}</div>
                            <div><span style="color:${resolveColor(CHART_COLORS.opportunity)};">●</span> Opportunities: ${breakdown.opportunity}</div>
                            <div><span style="color:${resolveColor(CHART_COLORS.weakness)};">●</span> Weaknesses: ${breakdown.weakness}</div>
                            <div><span style="color:${resolveColor(CHART_COLORS.threat)};">●</span> Threats: ${breakdown.threat}</div>
                          </div>
                        </div>
                      `;
                    }

                    return `<div style="background:${CHART_COLORS.white}; padding:10px; border: 1px solid ${CHART_COLORS.borderGray}; border-radius:4px;">
                      <div style="font-weight:bold; margin-bottom:5px;">${displayLabel}</div>
                      <div>Total Insights: ${rowData[2] || size}</div>
                      <div>Avg SWOT Value: ${value ? value.toFixed(1) : 'N/A'}</div>
                      ${swotBreakdown}
                    </div>`;
                  },
                }}
                height='400px'
              />
              <div className='mt-3 d-flex gap-3 align-items-center flex-wrap'>
                <span className='text-sm text-ThemeTextMedium strong'>Color Scale (SWOT):</span>
                <div className='d-flex align-items-center gap-2'>
                  <div style={{ width: '20px', height: '20px', backgroundColor: CHART_COLORS.treemapMin, border: `1px solid ${CHART_COLORS.borderGray}` }} />
                  <span className='text-xs'>Positive (Strengths & Opportunities)</span>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <div style={{ width: '20px', height: '20px', backgroundColor: CHART_COLORS.treemapMid, border: `1px solid ${CHART_COLORS.borderGray}` }} />
                  <span className='text-xs'>Neutral/Mixed</span>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <div style={{ width: '20px', height: '20px', backgroundColor: CHART_COLORS.treemapMax, border: `1px solid ${CHART_COLORS.borderGray}` }} />
                  <span className='text-xs'>Negative (Weaknesses & Threats)</span>
                </div>
                <span className='text-xs text-ThemeTextLight ms-3'>
                  (Strength=+2, Opportunity=+1, Weakness=-1, Threat=-2)
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Heatmaps Section */}
      <div className='py-4 border-top'>
        <div className='d-flex gap-2'>
          <div>
            <Badge className='background-ThemeDangerExtralight p-2'>
              <i className='text-ThemeDangerDark fal fa-2x fa-th' />
            </Badge>
          </div>
          <div className='d-flex flex-column ms-2'>
            <div className='text-xl strong'>Cross-Dimensional Analysis</div>
            <div className='text-ThemeTextMedium text-md'>
              Heatmap matrices showing insight distribution across multiple dimensions
            </div>
          </div>
        </div>
      </div>
      <div className='row g-4 mb-4'>
        {/* Impact x SWOT Matrix */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-th me-2 text-danger' />
                Impact x SWOT Matrix
              </h6>
              <p className='text-sm text-ThemeTextMedium mb-3'>
                Insight distribution by impact level and SWOT category. Click cells to view insights.
              </p>
              <InteractiveHeatmap
                insights={insights}
                rowKey='impact'
                colKey='swotCategory'
                rowLabels={['Critical', 'High', 'Medium', 'Low']}
                colLabels={['Strength', 'Weakness', 'Opportunity', 'Threat']}
                onCellClick={(cellInsights, rowLabel, colLabel) => {
                  setSelectedInsightsList(cellInsights);
                  setMatrixModalTitle(`${rowLabel} Impact × ${colLabel}`);
                }}
              />
            </div>
          </Card>
        </div>

        {/* EESG x SWOT Matrix */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-th me-2 text-success' />
                EESG x SWOT Matrix
              </h6>
              <p className='text-sm text-ThemeTextMedium mb-3'>
                Insight distribution by EESG category and SWOT analysis. Click cells to view insights.
              </p>
              <InteractiveHeatmap
                insights={insights}
                rowKey='sectionType'
                colKey='swotCategory'
                rowLabels={['Environment', 'Economic', 'Social', 'Governance']}
                colLabels={['Strength', 'Weakness', 'Opportunity', 'Threat']}
                onCellClick={(cellInsights, rowLabel, colLabel) => {
                  setSelectedInsightsList(cellInsights);
                  setMatrixModalTitle(`${rowLabel} × ${colLabel}`);
                }}
              />
            </div>
          </Card>
        </div>
      </div>

      <div className='py-4 border-top'>
        <div className='d-flex gap-2'>
          <div>
            <Badge className='background-ThemeAccentExtralight p-2'>
              <i className='text-ThemeAccentDark fal fa-2x fa-chart-pie' />
            </Badge>
          </div>
          <div className='d-flex flex-column ms-2'>
            <div className='text-xl strong'>Priority Analysis</div>
            <div className='text-ThemeTextMedium text-md'>
              Visual breakdown of insights across multiple priority dimensions ({insights.length} insights)
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className='row g-4 mb-4'>
        {/* Priority Distribution */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-exclamation-triangle me-2 text-danger' />
                Priority Distribution
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='PieChart'
                data={priorityDistribution}
                options={{
                  title: '',
                  pieHole: 0.4,
                  colors: [resolveColor(CHART_COLORS.critical), resolveColor(CHART_COLORS.high), resolveColor(CHART_COLORS.medium), resolveColor(CHART_COLORS.low)],
                  legend: { position: 'bottom' },
                  chartArea: { width: '90%', height: '75%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>

        {/* SWOT Distribution */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-chart-bar me-2 text-success' />
                SWOT Analysis Distribution
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='ColumnChart'
                data={swotDistribution}
                options={{
                  title: '',
                  legend: { position: 'none' },
                  vAxis: { title: 'Count', minValue: 0 },
                  hAxis: { title: '' },
                  chartArea: { width: '85%', height: '70%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>

        {/* Time Horizon */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-clock me-2 text-info' />
                Time Horizon Distribution
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='BarChart'
                data={timeHorizonDistribution}
                options={{
                  title: '',
                  legend: { position: 'none' },
                  hAxis: { title: 'Count', minValue: 0 },
                  vAxis: { title: '' },
                  chartArea: { width: '75%', height: '70%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>

        {/* Impact Assessment */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-chart-area me-2 text-warning' />
                Average Impact Assessment
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='ColumnChart'
                data={impactAssessment}
                options={{
                  title: '',
                  legend: { position: 'none' },
                  vAxis: { title: 'Score (0-10)', minValue: 0, maxValue: 10 },
                  hAxis: { title: '' },
                  chartArea: { width: '85%', height: '70%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>

        {/* Actions: Effort vs Impact Scatter */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-chart-scatter me-2 text-warning' />
                Actions: Effort vs Impact
              </h6>
              <p className='text-sm text-ThemeTextMedium mb-3'>
                Focus on low effort + high impact (upper left quadrant)
              </p>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='ScatterChart'
                data={actionsScatterData.data}
                options={{
                  title: '',
                  hAxis: { title: 'Effort Required', minValue: 0, maxValue: 10 },
                  vAxis: { title: 'Impact Level', minValue: 0, maxValue: 10 },
                  legend: { position: 'bottom' },
                  chartArea: { width: '80%', height: '70%' },
                  pointSize: 7,
                  colors: [resolveColor(CHART_COLORS.critical), resolveColor(CHART_COLORS.high), resolveColor(CHART_COLORS.medium), resolveColor(CHART_COLORS.lowSuccess)],
                }}
                height='300px'
                chartEvents={[
                  {
                    eventName: 'select',
                    callback: ({ chartWrapper }) => {
                      const chart = chartWrapper.getChart();
                      const selection = chart.getSelection();
                      if (selection.length > 0) {
                        const row = selection[0].row;
                        if (row !== null && row !== undefined) {
                          const insight = actionsScatterData.insights[row];
                          if (insight) {
                            setSelectedInsight(insight);
                          }
                        }
                      }
                    },
                  },
                ]}
              />
            </div>
          </Card>
        </div>

        {/* EESG Distribution */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-globe me-2 text-success' />
                EESG Category Distribution
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='ColumnChart'
                data={eesgDistribution}
                options={{
                  title: '',
                  legend: { position: 'none' },
                  vAxis: { title: 'Count', minValue: 0 },
                  hAxis: { title: '' },
                  chartArea: { width: '85%', height: '70%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>
      </div>

      {/* Actions Metrics Row */}
      <div className='py-4 border-top'>
        <div className='d-flex gap-2'>
          <div>
            <Badge className='background-ThemeWarningExtralight p-2'>
              <i className='text-ThemeWarningDark fal fa-2x fa-tasks' />
            </Badge>
          </div>
          <div className='d-flex flex-column ms-2'>
            <div className='text-xl strong'>Actions Analysis</div>
            <div className='text-ThemeTextMedium text-md'>
              Distribution of recommended actions by key dimensions
            </div>
          </div>
        </div>
      </div>
      <div className='row g-4 mb-4'>
        {/* Actions by Impact */}
        <div className='col-md-4'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-bullseye me-2 text-danger' />
                Actions by Impact
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='ColumnChart'
                data={actionsMetrics.impact}
                options={{
                  title: '',
                  legend: { position: 'none' },
                  vAxis: { title: 'Count', minValue: 0 },
                  hAxis: { title: '' },
                  chartArea: { width: '85%', height: '70%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>

        {/* Actions by Effort */}
        <div className='col-md-4'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-hammer me-2 text-warning' />
                Actions by Effort
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='ColumnChart'
                data={actionsMetrics.effort}
                options={{
                  title: '',
                  legend: { position: 'none' },
                  vAxis: { title: 'Count', minValue: 0 },
                  hAxis: { title: '' },
                  chartArea: { width: '85%', height: '70%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>

        {/* Actions by Control */}
        <div className='col-md-4'>
          <Card className='h-100'>
            <div className='p-3'>
              <h6 className='strong text-ThemeTextDark mb-3'>
                <i className='fas fa-hand-pointer me-2 text-success' />
                Actions by Control
              </h6>
              <Chart
                chartVersion={CHART_VERSION}
                chartType='ColumnChart'
                data={actionsMetrics.control}
                options={{
                  title: '',
                  legend: { position: 'none' },
                  vAxis: { title: 'Count', minValue: 0 },
                  hAxis: { title: '' },
                  chartArea: { width: '85%', height: '70%' },
                }}
                height='300px'
              />
            </div>
          </Card>
        </div>
      </div>

      {/* Top Priority Insights */}
      <Card className='mb-4'>
        <div className='p-3'>
          <h6 className='strong text-ThemeTextDark mb-3'>
            <i className='fas fa-trophy me-2 text-warning' />
            Top 10 Priority Insights
          </h6>
          <div className='table-responsive'>
            <table className='table table-hover'>
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  <th>Title</th>
                  <th style={{ width: '100px' }}>Score</th>
                  <th style={{ width: '100px' }}>Impact</th>
                  <th style={{ width: '150px' }}>SWOT</th>
                  <th style={{ width: '120px' }}>Control</th>
                </tr>
              </thead>
              <tbody>
                {topInsights.map((insight, index) => (
                  <tr key={insight._id} onClick={() => {
                    setSelectedInsight(insight);
                  }} style={{ cursor: 'pointer' }}>
                    <td>
                      <Badge color='secondary' className='rounded-circle' style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {index + 1}
                      </Badge>
                    </td>
                    <td className='text-ThemeTextDark strong'>{insight.content.title}</td>
                    <td>
                      <Badge color='primary' pill>
                        {insight.content.priorityScore.toFixed(1)}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={getPriorityColor(insight.content.impact)} pill>
                        {insight.content.impact}
                      </Badge>
                    </td>
                    <td>
                      <span className='text-capitalize text-ThemeTextMedium'>
                        {insight.content.swotCategory.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className='text-capitalize text-ThemeTextMedium'>
                        {insight.content.controlLevel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Unified Insight Modal */}
      {selectedInsight && (
        <InsightViewModal
          insight={selectedInsight}
          isOpen={true}
          onClose={() => setSelectedInsight(null)}
        />
      )}

      {/* Matrix Cell Insights List Modal */}
      {selectedInsightsList && (
        <Modal isOpen={!!selectedInsightsList} toggle={() => setSelectedInsightsList(null)} size='xl'>
          <ModalHeader toggle={() => setSelectedInsightsList(null)}>
            <div className='d-flex align-items-center gap-2'>
              <i className='fas fa-th text-primary' />
              <span>{matrixModalTitle} - {selectedInsightsList.length} Insights</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className='d-flex flex-column gap-3'>
              {selectedInsightsList.map((insight) => (
                <InsightCard
                  key={insight._id}
                  insight={insight}
                  truncateTitle={false}
                  truncateDescription={false}
                  onDetailsClick={() => {
                    setSelectedInsightsList(null);
                    setSelectedInsight(insight);
                  }}
                />
              ))}
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

// Interactive Heatmap Component
interface HeatmapProps {
  insights: InsightSynthesis[];
  rowKey: 'impact' | 'sectionType';
  colKey: 'swotCategory';
  rowLabels: string[];
  colLabels: string[];
  onCellClick: (insights: InsightSynthesis[], rowLabel: string, colLabel: string) => void;
}

const InteractiveHeatmap: React.FC<HeatmapProps> = ({
  insights,
  rowKey,
  colKey,
  rowLabels,
  colLabels,
  onCellClick,
}) => {
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);

  // Build matrix data
  const matrixData = useMemo(() => {
    const matrix: Record<string, Record<string, InsightSynthesis[]>> = {};
    const rowKeyMap: Record<string, string> = {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      environment: 'Environment',
      economic: 'Economic',
      social: 'Social',
      governance: 'Governance',
    };
    const colKeyMap: Record<string, string> = {
      strength: 'Strength',
      weakness: 'Weakness',
      opportunity: 'Opportunity',
      threat: 'Threat',
    };

    // Initialize matrix
    rowLabels.forEach((row) => {
      matrix[row] = {};
      colLabels.forEach((col) => {
        matrix[row][col] = [];
      });
    });

    // Populate matrix
    insights.forEach((insight) => {
      let rowValue: string;
      if (rowKey === 'impact') {
        rowValue = rowKeyMap[insight.content.impact];
      } else {
        rowValue = rowKeyMap[insight.source.metadata.sectionType];
      }
      const colValue = colKeyMap[insight.content.swotCategory];

      if (rowValue && colValue && matrix[rowValue] && matrix[rowValue][colValue]) {
        matrix[rowValue][colValue].push(insight);
      }
    });

    return matrix;
  }, [insights, rowKey, colKey, rowLabels, colLabels]);

  // Calculate max count for color scaling
  const maxCount = useMemo(() => {
    let max = 0;
    Object.values(matrixData).forEach((row) => {
      Object.values(row).forEach((cellInsights) => {
        max = Math.max(max, cellInsights.length);
      });
    });
    return max;
  }, [matrixData]);

  // Green to red: low count = green, high count = red
  const getCellColor = (count: number, isHovered: boolean) => {
    if (count === 0) return CHART_COLORS.lightGray;
    const intensity = count / maxCount; // 0 - green, 1 - red
    const { rgbGreen, rgbYellow, rgbRed } = CHART_COLORS;
    let r, g, b;

    if (intensity < 0.5) {
      const ratio = intensity / 0.5;
      r = Math.round(rgbGreen.r + (rgbYellow.r - rgbGreen.r) * ratio);
      g = Math.round(rgbGreen.g + (rgbYellow.g - rgbGreen.g) * ratio);
      b = Math.round(rgbGreen.b + (rgbYellow.b - rgbGreen.b) * ratio);
    } else {
      const ratio = (intensity - 0.5) / 0.5;
      r = Math.round(rgbYellow.r + (rgbRed.r - rgbYellow.r) * ratio);
      g = Math.round(rgbYellow.g + (rgbRed.g - rgbYellow.g) * ratio);
      b = Math.round(rgbYellow.b + (rgbRed.b - rgbYellow.b) * ratio);
    }

    return `rgba(${r}, ${g}, ${b}, ${isHovered ? 1 : 0.5})`;
  };

  return (
    <div className='table-responsive'>
      <table className='table table-bordered' style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: '120px', backgroundColor: CHART_COLORS.lightGray }}></th>
            {colLabels.map((col, colIndex) => (
              <th key={colIndex} className='text-center text-sm' style={{ backgroundColor: CHART_COLORS.lightGray }}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowLabels.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <td className='text-sm strong' style={{ backgroundColor: CHART_COLORS.lightGray }}>
                {row}
              </td>
              {colLabels.map((col, colIndex) => {
                const cellInsights = matrixData[row]?.[col] || [];
                const count = cellInsights.length;
                const isHovered = hoveredCell?.row === rowIndex && hoveredCell?.col === colIndex;

                return (
                  <td
                    key={colIndex}
                    className='text-center'
                    style={{
                      backgroundColor: getCellColor(count, isHovered),
                      cursor: count > 0 ? 'pointer' : 'default',
                      position: 'relative',
                      fontWeight: isHovered ? 'bold' : 'normal',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => count > 0 && onCellClick(cellInsights, row, col)}
                    onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                    onMouseLeave={() => setHoveredCell(null)}
                  >
                    <div className='text-lg strong'>{count}</div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
